import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import { MaterialReactTable } from "material-react-table";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Layout from "../../../layout/Layout";
import { proformaAPI, manageCompaniesAPI } from "../../../config";

function ProformaOrderList() {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const [proformas, setProformas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [searchString, setSearchString] = useState("");

  const [companyName, setCompanyName] = useState(0);
  const [companyNames, setCompanyNames] = useState([]);

  // Fetch company names
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.post(manageCompaniesAPI, { intType: 3 });
      if (response.data.success) {
        setCompanyNames([{ id: 0, company_name: "All" }, ...response.data.result]);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    }
  }, []);

  // Fetch Proforma list
  const fetchProformas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: "POST",
        url: proformaAPI,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + userData.token,
        },
        data: {
          intType: 3,
          intLimit: pageSize,
          intOffset: pageIndex * pageSize,
          txtSearchString: searchString,
          sorting: sorting,
          columnFilters: filters,
        },
      });

      if (response.data.success) {
        setProformas(response.data.result);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch Proforma Orders:", err);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, searchString, sorting, filters, userData.token]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchProformas();
  }, [fetchProformas]);

  const handleDeleteProforma = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this Proforma Order?");
    if (!isConfirmed) return;

    try {
      const response = await axios({
        method: "POST",
        url: proformaAPI,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + userData.token,
        },
        data: {
          intType: 7,
          proformaId: id,
        },
      });

      if (response.data.success) {
        alert(response.data.message);
        fetchProformas();
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete Proforma Order.");
    }
  };

  const handleEditProforma = (id) => {
    navigate(`/proforma-order/edit/${id}`);
  };

  const handleViewProforma = (id) => {
    navigate(`/proforma-order/view/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "proforma_no",
        header: "Proforma No",
        size: 180,
        Cell: ({ row }) => (
          <span
            style={{
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "bold"
            }}
            onClick={() => handleViewProforma(row.original.id)}
          >
            {row.original.proforma_no}
          </span>
        ),
      },
      {
        accessorKey: "company_Name",
        header: "Company Name",
        size: 200,
      },
      {
        accessorKey: "vendor_name",
        header: "Vendor Name",
        size: 200,
      },
      {
        accessorKey: "project_name",
        header: "Project Name",
        size: 200,
      },
      {
        accessorKey: "total_with_gst",
        header: "Total Value (GST)",
        size: 150,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toLocaleString("en-IN")}`,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => (
          <span style={{ fontWeight: "bold", color: cell.getValue() === "Approved" ? "green" : "orange" }}>
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        size: 150,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleEditProforma(row.original.id)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDeleteProforma(row.original.id)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [userData.token]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left" variant="h5" fontWeight="bold">
              Proforma Order List
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Purchase Order</Typography>
              <Typography color="text.primary">Proforma List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Search Proforma / Company / Vendor"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Type to search..."
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "40px",
                  paddingLeft: "20px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <Button
              style={{
                marginTop: 10,
                color: "white",
                backgroundColor: "red",
                padding: "10px 20px",
                borderRadius: "20px"
              }}
              onClick={() => setSearchString("")}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, background: "#fff", p: 2, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <MaterialReactTable
            columns={columns}
            data={proformas}
            enablePagination
            manualPagination
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const nextState = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
              setPageIndex(nextState.pageIndex);
              setPageSize(nextState.pageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            state={{
              pageIndex,
              pageSize,
              sorting,
              columnFilters: filters,
              isLoading: loading,
            }}
            muiToolbarAlertBannerProps={
              loading
                ? {
                    color: "info",
                    children: "Loading Proforma Orders...",
                  }
                : undefined
            }
          />
        </Box>
      </Grid>
    </Layout>
  );
}

export default ProformaOrderList;
